import { Group, Stack, TextInput } from '@mantine/core';
import { useUpdateLead, type Lead } from '@/models/leads';
import { IntlPhoneInput } from '@/components/IntlPhoneInput';
import { FormSaveBar } from '@/components/FormSaveBar';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { useCardForm } from '@/lib/useCardForm';

type Values = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  whatsapp: string;
  telegram: string;
};

const normPhone = (v: string | null | undefined) =>
  (v ?? '').replace(/\s+/g, '');

export function IdentityCard({
  lead,
  readOnly,
}: {
  lead: Lead;
  readOnly: boolean;
}) {
  const update = useUpdateLead();
  const { form, dirty, saving, handleSubmit } = useCardForm<Values>({
    initialValues: {
      firstName: lead.firstName ?? '',
      lastName: lead.lastName ?? '',
      email: lead.email ?? '',
      phone: normPhone(lead.phone),
      whatsapp: normPhone(lead.whatsapp),
      telegram: lead.telegram ?? '',
    },
    enabled: !readOnly,
    save: (dirtyValues) => update.mutateAsync({ id: lead.id, ...dirtyValues }),
    successMessage: 'Identity saved',
  });

  return (
    <CollapsibleCard title="Identity info">
      <form onSubmit={handleSubmit}>
        <Stack gap="xs">
          <Group grow gap="xs">
            <TextInput
              label="First name"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('firstName')}
            />
            <TextInput
              label="Last name"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('lastName')}
            />
          </Group>
          <Group grow gap="xs">
            <TextInput
              label="Email"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('email')}
            />
            <TextInput
              label="Telegram"
              size="xs"
              disabled={readOnly}
              {...form.getInputProps('telegram')}
            />
          </Group>
          <Group grow gap="xs">
            <IntlPhoneInput
              label="Phone"
              size="xs"
              value={form.values.phone}
              disabled={readOnly}
              onChange={(v) => form.setFieldValue('phone', normPhone(v))}
            />
            <IntlPhoneInput
              label="WhatsApp"
              size="xs"
              value={form.values.whatsapp}
              disabled={readOnly}
              onChange={(v) => form.setFieldValue('whatsapp', normPhone(v))}
            />
          </Group>
          {dirty && !readOnly && (
            <FormSaveBar saving={saving} onDiscard={form.reset} />
          )}
        </Stack>
      </form>
    </CollapsibleCard>
  );
}
